// Compatibility shim. The legacy `userInfo.role.privilege` JSON
// (canEdit/canDelete/canView) is gone — actual permissions are now driven by
// rbac_role_assignment on the backend and exposed via `usePermission`.
//
// Existing call sites pass strings like "canEdit" / "canDelete" / "canView".
// We translate those into the closest equivalent permission predicate so we
// don't have to rewrite every component at once.
//
// Long-term: replace usePrivilege() calls with usePermission().can(<code>, <projectId>).

import { useCallback, useMemo } from "react";
import usePermission from "./usePermission";
import { PERMISSIONS } from "../constants/permissions";

const usePrivilege = () => {
  const { can, isDeveloperAdmin } = usePermission();

  const hasPrivilege = useCallback(
    (privilege) => {
      if (isDeveloperAdmin()) return true;
      // Map the three legacy generic flags onto representative new permission codes.
      // (Components that need finer-grained checks should migrate to usePermission.)
      switch (privilege) {
        case "canEdit":
          return can(PERMISSIONS.PROJECT_EDIT) || can(PERMISSIONS.PROPERTY_EDIT) || can(PERMISSIONS.LEAD_EDIT);
        case "canDelete":
          return can(PERMISSIONS.PROPERTY_DELETE) || can(PERMISSIONS.LEAD_DELETE) || can(PERMISSIONS.CHANNEL_PARTNER_DELETE);
        case "canView":
          return true;
        case "canCreate":
          return can(PERMISSIONS.PROJECT_CREATE) || can(PERMISSIONS.LEAD_CREATE) || can(PERMISSIONS.SALE_CREATE);
        default:
          return false;
      }
    },
    [can, isDeveloperAdmin]
  );

  return useMemo(() => ({ hasPrivilege }), [hasPrivilege]);
};

export default usePrivilege;
